import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Plug, Settings2, XCircle } from 'lucide-react';
import type { PluginStatus } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, EmptyState, ErrorState, Skeleton, useFetch, useToast } from '../components/ui';

export function PluginsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [settingsFor, setSettingsFor] = useState<PluginStatus | null>(null);

  const { data, loading, error, reload } = useFetch(() => api.get<PluginStatus[]>('/api/plugins'), [], {
    refreshMs: 30_000,
  });

  const toggle = async (plugin: PluginStatus, enabled: boolean) => {
    try {
      await api.post(`/api/plugins/${plugin.manifest.id}/enabled`, { enabled });
      toast(`${plugin.manifest.name} ${enabled ? 'enabled' : 'disabled'}`, 'good');
      reload();
    } catch (err) {
      toast('Could not change the plugin state', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const plugins = data ?? [];

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Plugins</h1>
          <div className="page-subtitle">
            {plugins.filter((p) => p.state === 'loaded').length} of {plugins.length} loaded
            {plugins.some((p) => p.state === 'error') && ` · ${plugins.filter((p) => p.state === 'error').length} failed`}
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="grid-auto">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={168} />
          ))}
        </div>
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : plugins.length === 0 ? (
        <EmptyState icon={<Plug size={20} />} title="No plugins found" text="Drop a plugin folder into ./plugins and restart the server." />
      ) : (
        <div className="grid-auto">
          {plugins
            // Broken plugins first: they are the ones that need attention, and
            // burying them alphabetically is how an integration stays dead for
            // a week without anyone noticing.
            .sort((a, b) => stateRank(a.state) - stateRank(b.state) || a.manifest.order - b.manifest.order)
            .map((plugin) => (
              <div className="card" key={plugin.manifest.id}>
                <div className="card-header">
                  <StateIcon state={plugin.state} />
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="card-title truncate">{plugin.manifest.name}</div>
                    <div className="card-subtitle">v{plugin.manifest.version}</div>
                  </div>
                  {can('plugin:manage') && plugin.manifest.settings.length > 0 && plugin.state === 'loaded' && (
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      onClick={() => setSettingsFor(plugin)}
                      title="Settings"
                      aria-label={`${plugin.manifest.name} settings`}
                    >
                      <Settings2 size={14} />
                    </button>
                  )}
                </div>

                <div className="card-body">
                  <p className="subtle" style={{ fontSize: 12.5, lineHeight: 1.5, minHeight: 38 }}>
                    {plugin.manifest.description}
                  </p>

                  {plugin.error && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: '8px 10px',
                        borderRadius: 'var(--itr-radius-sm)',
                        background: 'color-mix(in srgb, var(--itr-critical) 11%, transparent)',
                        color: 'var(--itr-critical)',
                        fontSize: 12,
                        lineHeight: 1.45,
                      }}
                    >
                      {plugin.error}
                    </div>
                  )}

                  <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 12 }}>
                    <span className="subtle">
                      <strong style={{ color: 'var(--itr-text)' }}>{plugin.resourceCount}</strong> resources
                    </span>
                    <span className="subtle">
                      <strong style={{ color: 'var(--itr-text)' }}>{plugin.manifest.widgets.length}</strong> widgets
                    </span>
                    {plugin.collectionErrors > 0 && (
                      <span style={{ color: 'var(--itr-warning)' }}>{plugin.collectionErrors} collect errors</span>
                    )}
                  </div>

                  {plugin.manifest.permissions.length > 0 && (
                    <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
                      {plugin.manifest.permissions.slice(0, 4).map((permission) => (
                        <span
                          key={permission}
                          className="badge"
                          style={{ height: 17, fontSize: 10 }}
                          // Sensitive capabilities are called out: an operator
                          // should know before enabling a third-party plugin
                          // that it can run shell commands.
                          data-intent={permission === 'exec:shell' || permission === 'billing:write' ? 'warn' : undefined}
                        >
                          {permission}
                        </span>
                      ))}
                      {plugin.manifest.permissions.length > 4 && (
                        <span className="badge" style={{ height: 17, fontSize: 10 }}>
                          +{plugin.manifest.permissions.length - 4}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="row" style={{ marginTop: 14 }}>
                    <Badge intent={plugin.state === 'loaded' ? 'good' : plugin.state === 'error' ? 'bad' : 'neutral'}>
                      {plugin.state}
                    </Badge>
                    {plugin.lastCollectionAt && (
                      <span className="subtle" style={{ fontSize: 11.5, marginLeft: 8 }}>
                        collected {formatRelative(plugin.lastCollectionAt)}
                      </span>
                    )}
                    <span className="grow" />
                    {can('plugin:manage') && plugin.state !== 'error' && (
                      <label className="row" style={{ gap: 6, cursor: 'pointer', fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={plugin.state === 'loaded'}
                          onChange={(e) => void toggle(plugin, e.target.checked)}
                        />
                        Enabled
                      </label>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      <Drawer open={Boolean(settingsFor)} onClose={() => setSettingsFor(null)} title={`${settingsFor?.manifest.name} settings`}>
        {settingsFor && (
          <PluginSettingsForm
            plugin={settingsFor}
            onSaved={() => {
              setSettingsFor(null);
              reload();
              toast('Settings saved', 'good');
            }}
          />
        )}
      </Drawer>
    </>
  );
}

function StateIcon({ state }: { state: string }) {
  if (state === 'loaded') return <CheckCircle2 size={16} style={{ color: 'var(--itr-success)', flexShrink: 0 }} />;
  if (state === 'error') return <XCircle size={16} style={{ color: 'var(--itr-critical)', flexShrink: 0 }} />;
  return <AlertTriangle size={16} style={{ color: 'var(--itr-text-subtle)', flexShrink: 0 }} />;
}

function stateRank(state: string): number {
  return state === 'error' ? 0 : state === 'loaded' ? 1 : 2;
}

function PluginSettingsForm({ plugin, onSaved }: { plugin: PluginStatus; onSaved: () => void }) {
  const toast = useToast();
  const { data, loading } = useFetch(
    () => api.get<Record<string, unknown>>(`/api/plugins/${plugin.manifest.id}/settings`),
    [plugin.manifest.id],
  );
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  const current = { ...(data ?? {}), ...values };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/api/plugins/${plugin.manifest.id}/settings`, current);
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton height={200} />;

  return (
    <form onSubmit={submit}>
      {plugin.manifest.settings.map((setting) => (
        <div className="field" key={setting.key}>
          <label className="field-label">{setting.label}</label>

          {setting.type === 'boolean' ? (
            <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={Boolean(current[setting.key])}
                onChange={(e) => setValues((v) => ({ ...v, [setting.key]: e.target.checked }))}
              />
              <span className="subtle" style={{ fontSize: 12.5 }}>
                Enabled
              </span>
            </label>
          ) : setting.type === 'select' ? (
            <select
              className="select"
              value={String(current[setting.key] ?? '')}
              onChange={(e) => setValues((v) => ({ ...v, [setting.key]: e.target.value }))}
            >
              {setting.choices?.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          ) : setting.type === 'textarea' ? (
            <textarea
              className="textarea"
              value={String(current[setting.key] ?? '')}
              onChange={(e) => setValues((v) => ({ ...v, [setting.key]: e.target.value }))}
            />
          ) : (
            <input
              className="input"
              type={setting.type === 'password' ? 'password' : setting.type === 'number' ? 'number' : 'text'}
              value={String(current[setting.key] ?? '')}
              onChange={(e) =>
                setValues((v) => ({
                  ...v,
                  [setting.key]: setting.type === 'number' ? Number(e.target.value) : e.target.value,
                }))
              }
              autoComplete={setting.secret ? 'new-password' : undefined}
            />
          )}

          {setting.help && <span className="field-help">{setting.help}</span>}
          {setting.secret && <span className="field-help">Stored encrypted. Leave the mask untouched to keep the current value.</span>}
        </div>
      ))}

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save settings'}
      </button>
    </form>
  );
}
